import React from "react";
import { InlineMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";

/* ===== helpers ===== */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

// ładny zapis czynnika: r=0 -> "x", r>0 -> "(x - r)", r<0 -> "(x + |r|)"
const factorLatex = (r) => (r === 0 ? "x" : `(${r >= 0 ? `x - ${r}` : `x + ${Math.abs(r)}`})`);

// budowa LaTeX-a: jeśli któryś pierwiastek = 0, to stawiamy "x" jako pierwszy czynnik
const inequalityLatex = (a, x1, x2, kind) => {
  const sign = SIGN_LATEX[kind];
  let f1, f2;
  if (x1 === 0) [f1, f2] = [factorLatex(0), factorLatex(x2)];
  else if (x2 === 0) [f1, f2] = [factorLatex(0), factorLatex(x1)];
  else [f1, f2] = [factorLatex(x1), factorLatex(x2)];
  return `${a}\\,${f1}\\,${f2}\\;${sign}\\;0`;
};

/* ===== generator ===== */
export function generateTask(options) {
  const forceKind = typeof options === "string" ? options : options?.kind;
  const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // a ∈ {-5..-2, 2..5} bez 0 i bez ±1
  let a = 0;
  while (a === 0 || Math.abs(a) === 1) a = rnd(-5, 5);

  // dwa różne pierwiastki
  let x1 = rnd(-6, 6), x2 = rnd(-6, 6);
  while (x1 === x2) { x2 = rnd(-6, 6); }

  const kind = forceKind ?? SIGN_KINDS[rnd(0, SIGN_KINDS.length - 1)];
  const latex = inequalityLatex(a, x1, x2, kind);

  const r1 = Math.min(x1, x2);
  const r2 = Math.max(x1, x2);

  return {
    type: "QuadraticFactoredInequalityTask",
    data: { a, x1, x2, r1, r2, kind, latex },
  };
}

/* ===== UI ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <InlineMath math={data.latex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;\\in"} />
        </div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 1) ∪ (4, ∞) albo (1, 4)"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw =
    typeof userInput === "string"
      ? userInput
      : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== logika rozwiązania ===== */
function solutionFor({ a, r1, r2, kind }) {
  const inclusive = kind === "le" || kind === "ge";
  const needOutside = (kind === "gt" || kind === "ge") ? (a > 0) : (a < 0);
  if (needOutside) {
    return {
      kind: "outside",
      inclusive,
      left:  { L: -Infinity, R: r1, Rclosed: inclusive },
      right: { L: r2,        R: Infinity, Lclosed: inclusive }
    };
  }
  return {
    kind: "between",
    inclusive,
    seg: { L: r1, R: r2, Lclosed: inclusive, Rclosed: inclusive }
  };
}

function intervalsEqual(ans, parsed) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const eq = (a, b, tol = 1e-9) => Math.abs(a - b) <= tol;
  const norm = (I) => ({ L: I.left, R: I.right, Lc: !!I.leftClosed, Rc: !!I.rightClosed });

  const expected = [];
  if (ans.kind === "outside") {
    expected.push({ L: -Infinity, R: ans.left.R,  Lc:false, Rc:!!ans.left.Rclosed });
    expected.push({ L: ans.right.L, R: Infinity, Lc:!!ans.right.Lclosed, Rc:false });
  } else {
    expected.push({ L: ans.seg.L, R: ans.seg.R, Lc:!!ans.seg.Lclosed, Rc:!!ans.seg.Rclosed });
  }

  const got = parsed.intervals.map(norm);
  if (expected.length !== got.length) return false;

  const used = new Array(got.length).fill(false);
  for (const E of expected) {
    let matched = false;
    for (let i=0;i<got.length;i++) {
      if (used[i]) continue;
      const G = got[i];
      const leftOK  = (E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L));
      const rightOK = (E.R ===  Infinity ? G.R ===  Infinity : eq(E.R, G.R));
      if (leftOK && rightOK && E.Lc === G.Lc && E.Rc === G.Rc) { used[i]=true; matched=true; break; }
    }
    if (!matched) return false;
  }
  return true;
}

export function checkAnswer({ data }, userInput = {}) {
  const raw =
    typeof userInput?.S === "string"
      ? userInput.S
      : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionFor({ a: data.a, r1: data.r1, r2: data.r2, kind: data.kind });
  return { correct: intervalsEqual(ans, parsed) };
}

/* ===== poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const ans = solutionFor(data);
  const { r1, r2 } = data;
  const rb = ans.inclusive ? "]" : ")";
  const lb = ans.inclusive ? "[" : "(";
  if (ans.kind === "outside") {
    return <InlineMath math={`x\\in (-\\infty,\\; ${r1}${rb}\\;\\cup\\; ${lb}${r2},\\; \\infty)`} />;
  }
  return <InlineMath math={`x\\in ${lb}${r1},\\; ${r2}${rb}`} />;
}

/* ===== wyjaśnienie ===== */
export function renderExplanation({ data }) {
  const { a, x1, x2, r1, r2, kind, latex } = data;
  const ans = solutionFor({ a, r1, r2, kind });

  const aPositive = a > 0;
  const armsTxt = aPositive ? "ramiona do góry" : "ramiona w dół";
  const chooseTxt =
    kind === "gt" || kind === "ge"
      ? "bierzemy część wykresu powyżej osi OX"
      : "bierzemy część wykresu poniżej osi OX";
  const inclusiveNote = ans.inclusive
    ? "Ponieważ znak jest „≤/≥”, miejsca zerowe także należą do rozwiązania."
    : "Miejsca zerowe nie należą do rozwiązania (otwarte końce).";

  const solLatex =
    ans.kind === "outside"
      ? String.raw`x\in (-\infty,\; ${r1}${ans.inclusive ? "]" : ")"}\ \cup\ ${ans.inclusive ? "[" : "("}${r2},\; \infty)`
      : String.raw`x\in ${ans.inclusive ? "[" : "("}${r1},\; ${r2}${ans.inclusive ? "]" : ")"}`;

  return (
    <div className="space-y-3">
      <p>
        <strong>Krok 1:</strong> Odczytujemy współczynnik <b>a</b> i
        miejsca zerowe, szkicujemy uproszczony wykres funkcji
        kwadratowej, a następnie decydujemy, które fragmenty wykresu
        bierzemy (nad/pod osią).
      </p>

      <ul style={{ marginLeft: "1.2rem" }}>
        <li>
          <InlineMath math={`a = ${a}`} /> — {aPositive ? "dodatni" : "ujemny"} (
          <InlineMath math={aPositive ? "a>0" : "a<0"} />) — {armsTxt}.
        </li>
        <li>
          <b>Miejsca zerowe:</b> <InlineMath math={`x = ${x1}`} /> oraz{" "}
          <InlineMath math={`x = ${x2}`} />.
        </li>
        <li>
          <b>Szukamy, gdzie</b> <InlineMath math={latex} />. Zatem {chooseTxt} (
          <InlineMath math={"f(x) > 0"} /> lub <InlineMath math={"f(x) < 0"} /> — w zależności od znaku). {inclusiveNote}
        </li>
      </ul>

      <div style={{ maxWidth: 620, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={a > 0 ? 1 : -1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          labelLeftTex={`${r1}`}
          labelRightTex={`${r2}`}
          highlightMode={ans.kind === "outside" ? "outside" : "between"}
          highlightLeftClosed={ans.inclusive}
          highlightRightClosed={ans.inclusive}
          highlightColor="#a78bfa"
        />
      </div>

      <p>
        <strong>Krok 2:</strong> Na podstawie szkicu odczytujemy
        przedział rozwiązań — to jest odpowiedź — i zaznaczamy go na osi
        liczbowej.
      </p>

      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <InlineMath math={solLatex} />
      </div>

      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        {ans.kind === "outside" ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to: r1, rightClosed: ans.inclusive, rightLabelLatex: `${r1}` },
              { from: r2, leftClosed: ans.inclusive, leftLabelLatex: `${r2}` },
            ]}
          />
        ) : (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              {
                from: r1,
                to: r2,
                leftClosed: ans.inclusive,
                rightClosed: ans.inclusive,
                leftLabelLatex: `${r1}`,
                rightLabelLatex: `${r2}`,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
